/**
 * Compare Models
 * ADDED: 2026-08-03 by MeshPlanet
 *
 * <compare-models> only answers one question: is there more table to the right?
 * The fade on the right edge is the answer, and it has to be able to disappear.
 * The design fits three columns inside a 360px screen, so most phones never
 * scroll at all — a fade that were always painted would read as a permanent
 * gradient over the last column rather than as a hint.
 *
 * Which is also why this can't be pure CSS. The classic scrolling-shadow trick
 * puts the gradients on the scroller's own background, and the table cells here
 * paint their own (striped) backgrounds straight over them.
 *
 * Data attributes:
 *   data-compare-scroller  the element that scrolls horizontally
 *   data-compare-overflow  set on the host while content remains to the right;
 *                          the CSS keys the fade's opacity off it
 */
(function () {
  'use strict';

  class CompareModels extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.scroller = this.querySelector('[data-compare-scroller]');
      if (!this.scroller) return;

      this.update = this.update.bind(this);

      // passive: the handler only reads geometry, so it must never hold up the
      // scroll it is reporting on.
      this.scroller.addEventListener('scroll', this.update, { passive: true });

      // Catches the breakpoint change and any late layout shift — a lazy product
      // image landing, or the theme editor re-rendering the section around us.
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.scroller);

      this.update();
    }

    disconnectedCallback() {
      this.scroller?.removeEventListener('scroll', this.update);
      this.observer?.disconnect();
      this.bound = false;
    }

    update() {
      const { scrollWidth, clientWidth, scrollLeft } = this.scroller;

      // scrollLeft is negative in RTL, and runs past the end during an elastic
      // overscroll on iOS. Sub-pixel rounding also leaves a stray pixel of
      // "remaining" on tables that actually fit, so a pixel counts as none.
      const remaining = scrollWidth - clientWidth - Math.abs(scrollLeft);

      this.toggleAttribute('data-compare-overflow', remaining > 1);
    }
  }

  if (!customElements.get('compare-models')) {
    customElements.define('compare-models', CompareModels);
  }
})();
